// lib
import React from 'react'
import { RouteComponentProps } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'

// constants & interfaces
import {
  INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION,
  INFRASTRUCTURE_PROJECTSFEED_TITLE,
  PROJECTSFEED_DESCRIPTION,
  PROJECTSFEED_TITLE
} from 'src/constants/meta_tags'

// helpers
import { skipToken } from '@apollo/client/react'
import { ProjectSortingKeys, ProjectType, SortDirection, useProjects } from '@lopo-tech/lopo-api'
import ProjectsOverview from 'src/components/projects/projects_overview/projects_overview'
import { INFRASTRUCTURE_PROJECTS, PUBLIC_PARTICIPATION } from 'src/constants/feed'
import { getProjectRoutes } from 'src/constants/urls'
import { IMapGeoJsonLayer } from 'src/interfaces/map'
import { textResources } from 'src/lang/de'
import LayoutMapContainer from 'src/layout/layout_map_container'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { boundingBoxToBoundingBoxInput } from 'src/utils/location'
import LayoutNoMap from '../layout/layout_no_map'

const t = textResources.projects

interface IProps extends RouteComponentProps {
  projectType: ProjectType;
}

export const ProjectsOverviewPage: React.FC<IProps> = ({ projectType }) => {
  const locationShape = useGetAppLocationShape()
  const routes = getProjectRoutes(projectType)
  const isInfrastructureProject = projectType === ProjectType.InfrastructureProject
  const titleResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_TITLE : PROJECTSFEED_TITLE
  const descriptionResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION : PROJECTSFEED_DESCRIPTION
  const title = locationShape ? titleResolver(locationShape.name) : undefined
  const description = locationShape ? descriptionResolver(locationShape.name) : undefined

  const { data, loading, error, refetch } = useProjects(
    locationShape
      ? {
        variables: {
          locationShapes: [locationShape.id],
          type: projectType,
          boundingBox: boundingBoxToBoundingBoxInput(locationShape.boundingBox),
          page: 1,
          perPage: 5,
          sorting: [{
            direction: SortDirection.Desc,
            fieldName: ProjectSortingKeys.LastActivityAt
          }]
        }
      }
      : skipToken
  )

  const projects = data?.projects?.items
  const totalCount = data?.projects?.pageInfo?.totalCount ?? 0

  const geoJsonLayers: IMapGeoJsonLayer[] = (projects ?? [])
    .filter((p) => !!p.geojson)
    .map((p) => ({
      id: p.id,
      geojson: p.geojson,
      url: routes.detail(p.slug)
    }))

  const Layout = projectType === ProjectType.PublicParticipationProject ? LayoutNoMap : LayoutMapContainer

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        og_url={document.location.toString()}
        title={title}
      />
      <Layout
        pageTitle={
          <StandardPageTitle
            text={isInfrastructureProject ? t.common.infrastructureProjects : t.common.projects}
            showLocation
          />
        }
        markers={[]}
        locationShape={locationShape}
        activeNavigationItem={projectType === ProjectType.PublicParticipationProject ? PUBLIC_PARTICIPATION : INFRASTRUCTURE_PROJECTS}
        geoJsonLayers={geoJsonLayers}
      >
        <ProjectsOverview
          projectType={projectType}
          projects={projects}
          totalCount={totalCount}
          loading={loading}
          isError={Boolean(error)}
          refetch={refetch}
        />
      </Layout>
    </>
  )
}

const PublicParticipationProjectsOverviewPage: React.FC<RouteComponentProps> = (props) => (
  <ProjectsOverviewPage {...props} projectType={ProjectType.PublicParticipationProject} />
)

export default PublicParticipationProjectsOverviewPage
